import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import ProductCard from "@/components/GameCard";
import { Search, X } from "lucide-react";

export default function Games() {
  const { t } = useLang();
  const [query, setQuery] = useState("");
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      (p.title || "").toLowerCase().includes(q) ||
      (p.platform || "").toLowerCase().includes(q) ||
      (p.description || "").toLowerCase().includes(q)
    );
  }, [products, query]);

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="games-page">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-3">{t("nav.games")}</div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">{t("sections.popular_sub")}</h1>

        <div className="relative max-w-xl">
          <Search className="w-4 h-4 absolute start-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("games.search_placeholder")}
            className="w-full bg-[#0c0c0c] border border-white/10 rounded-lg ps-11 pe-10 py-3 text-sm focus:border-brand outline-none transition-colors"
            data-testid="games-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute end-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              data-testid="games-search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {query && (
          <div className="mt-3 text-xs text-zinc-500" data-testid="games-search-count">
            {filtered.length} {t("games.results")}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-zinc-500">{t("common.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-12 text-center text-zinc-500" data-testid="games-empty">
          {query ? t("games.no_results") : t("common.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </main>
  );
}
